/**
 * Admin Content Approvals page — v43-1-4
 *
 * Lists pending content drafts (videos + glossary terms) produced by
 * openclaw agents (v43-1-1 how-to-videos + v43-1-2 glossary-pipeline-v2)
 * and lets the admin approve or reject each.
 *
 * Route: /admin/content-approvals
 * Backend: v43-1-3 /admin/content-approvals endpoints.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  Video,
  BookOpen,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  AlertTriangle,
  ShieldOff,
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

type ItemType = 'video' | 'glossary';

// v43-1-6: ib agent technical review shape
interface IbReview {
  verdict: 'clear' | 'flagged';
  notes: string;
  issues: string[];
}

interface ApprovalItem {
  id: string;
  type: ItemType;
  title: string;
  summary: string;
  created_at: string | null;
  full_text?: string | null; // v43-1-5: full glossary def or video description
  ib_review?: IbReview | null; // v43-1-6
}

interface ApprovalQueueResponse {
  pending_videos: number;
  pending_glossary: number;
  items: ApprovalItem[];
}

async function fetchQueue(token: string): Promise<ApprovalQueueResponse> {
  const res = await fetch(`${API_URL}/api/v1/admin/content-approvals`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Fetch queue failed: ${res.status}`);
  return res.json();
}

async function approveItem(token: string, type: ItemType, id: string): Promise<void> {
  const path = type === 'video' ? 'videos' : 'glossary';
  const res = await fetch(`${API_URL}/api/v1/admin/content-approvals/${path}/${id}/approve`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Approve failed: ${res.status}`);
}

async function fetchVideoPreviewUrl(token: string, id: string): Promise<string> {
  const res = await fetch(
    `${API_URL}/api/v1/admin/content-approvals/videos/${id}/preview-url`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Preview-url failed: ${res.status}`);
  const body = await res.json();
  return body.url;
}

async function rejectItem(
  token: string,
  type: ItemType,
  id: string,
  reason: string,
): Promise<void> {
  const path = type === 'video' ? 'videos' : 'glossary';
  const res = await fetch(
    `${API_URL}/api/v1/admin/content-approvals/${path}/${id}/reject`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ rejection_reason: reason || null }),
    },
  );
  if (!res.ok) throw new Error(`Reject failed: ${res.status}`);
}

type ActiveTab = 'all' | 'video' | 'glossary';

export default function AdminContentApprovals(): React.ReactElement {
  // Canonical auth key is 'token' (matches AuthContext + glossaryService).
  // Previously used 'access_token' which broke auth → 401. (hotfix-1-118)
  const token = localStorage.getItem('token') || '';
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ActiveTab>('all');
  const [rejectTarget, setRejectTarget] = useState<ApprovalItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data, isLoading, error } = useQuery<ApprovalQueueResponse>({
    queryKey: ['admin-content-approvals'],
    queryFn: () => fetchQueue(token),
    refetchInterval: 60_000, // refresh minute-by-minute so batches appear
  });

  const approveMutation = useMutation({
    mutationFn: ({ type, id }: { type: ItemType; id: string }) =>
      approveItem(token, type, id),
    onSuccess: () => {
      toast.success('Approved — now live');
      queryClient.invalidateQueries({ queryKey: ['admin-content-approvals'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ type, id, reason }: { type: ItemType; id: string; reason: string }) =>
      rejectItem(token, type, id, reason),
    onSuccess: () => {
      toast.success('Rejected');
      setRejectTarget(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['admin-content-approvals'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = (data?.items || []).filter((i) => tab === 'all' || i.type === tab);
  const counts = {
    all: (data?.pending_videos || 0) + (data?.pending_glossary || 0),
    video: data?.pending_videos || 0,
    glossary: data?.pending_glossary || 0,
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-6 h-6" />
          Content Approvals
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Drafts produced by openclaw agents. Approve to publish, or reject with a reason.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200 mb-4">
        {(['all', 'video', 'glossary'] as ActiveTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'all' && 'All'}
            {t === 'video' && 'Videos'}
            {t === 'glossary' && 'Glossary'}
            <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
              {counts[t]}
            </span>
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4">
          {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          No pending items. All caught up.
        </div>
      )}

      <ul className="space-y-3">
        {items.map((item) => (
          <ApprovalRow
            key={`${item.type}:${item.id}`}
            item={item}
            token={token}
            onApprove={() => approveMutation.mutate({ type: item.type, id: item.id })}
            onRejectClick={() => {
              setRejectTarget(item);
              setRejectReason('');
            }}
            approving={approveMutation.isPending}
          />
        ))}
      </ul>

      {rejectTarget !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-2">Reject {rejectTarget.type}</h2>
            <p className="text-sm text-gray-600 mb-3">
              <strong>{rejectTarget.title}</strong>
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason (optional, helps the content pipeline avoid similar output)"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
            <div className="flex gap-3 mt-4 justify-end">
              <button
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rejectMutation.mutate({
                    type: rejectTarget.type,
                    id: rejectTarget.id,
                    reason: rejectReason.trim(),
                  })
                }
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single approval row — handles its own expand state + lazy preview fetch.
// v43-1-5: extracted so each row can show a video player or full glossary text
// without making the parent re-render its whole list when one item expands.
// ---------------------------------------------------------------------------

interface ApprovalRowProps {
  item: ApprovalItem;
  token: string;
  onApprove: () => void;
  onRejectClick: () => void;
  approving: boolean;
}

function ApprovalRow({
  item,
  token,
  onApprove,
  onRejectClick,
  approving,
}: ApprovalRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  // Only fetch the signed URL when the row is expanded AND the item is a video.
  const previewQuery = useQuery({
    queryKey: ['admin-content-approvals', 'preview-url', item.id],
    queryFn: () => fetchVideoPreviewUrl(token, item.id),
    enabled: expanded && item.type === 'video',
    staleTime: 14 * 60_000, // signed URL TTL is 15 min by default; refetch a min before
  });

  return (
    <li className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                item.type === 'video'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-purple-100 text-purple-800'
              }`}
            >
              {item.type === 'video' ? (
                <Video className="w-3 h-3" />
              ) : (
                <BookOpen className="w-3 h-3" />
              )}
              {item.type}
            </span>
            <IbReviewBadge review={item.ib_review} />
            <span className="text-xs text-gray-400 font-mono">{item.id.slice(0, 8)}…</span>
          </div>
          <h3 className="font-semibold text-gray-900">{item.title}</h3>
          {!expanded && (
            <p className="text-sm text-gray-600 mt-1">{item.summary}</p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
            title={expanded ? 'Hide preview' : 'Show preview'}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide' : 'Preview'}
          </button>
          <button
            onClick={onApprove}
            disabled={approving}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            Approve
          </button>
          <button
            onClick={onRejectClick}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700"
          >
            <XCircle className="w-4 h-4" />
            Reject
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {item.ib_review?.verdict === 'flagged' && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-1">
                <AlertTriangle className="w-4 h-4" />
                IB agent flagged {item.ib_review.issues.length} issue
                {item.ib_review.issues.length === 1 ? '' : 's'}
              </div>
              {item.ib_review.notes && (
                <p className="text-sm text-amber-900 mb-2 whitespace-pre-wrap">
                  {item.ib_review.notes}
                </p>
              )}
              {item.ib_review.issues.length > 0 && (
                <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5">
                  {item.ib_review.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {item.type === 'video' && (
            <>
              {previewQuery.isLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading video…
                </div>
              )}
              {previewQuery.error && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
                  Could not load video: {(previewQuery.error as Error).message}
                </div>
              )}
              {previewQuery.data && (
                <video
                  src={previewQuery.data}
                  controls
                  preload="metadata"
                  className="w-full max-h-[480px] rounded-lg bg-black"
                />
              )}
              {item.full_text && (
                <p className="text-sm text-gray-600 mt-3 whitespace-pre-wrap">{item.full_text}</p>
              )}
            </>
          )}
          {item.type === 'glossary' && (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {item.full_text || item.summary}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// v43-1-6: IB agent review badge — shown next to the type pill on each row.
// ---------------------------------------------------------------------------

function IbReviewBadge({
  review,
}: {
  review: IbReview | null | undefined;
}): React.ReactElement {
  if (!review) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600"
        title="IB agent did not review this draft"
      >
        <ShieldOff className="w-3 h-3" />
        IB: not reviewed
      </span>
    );
  }
  if (review.verdict === 'clear') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800"
        title={review.notes}
      >
        <ShieldCheck className="w-3 h-3" />
        IB: Clear
      </span>
    );
  }
  // flagged
  const n = review.issues.length;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800"
      title={review.notes}
    >
      <AlertTriangle className="w-3 h-3" />
      IB: Flagged ({n} issue{n === 1 ? '' : 's'})
    </span>
  );
}
